import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzModalRef } from "ng-zorro-antd/modal";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { sha256 } from "js-sha256";
import { AuthService } from "src/app/services/auth.service";

function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const newPassword = control.get("newPassword")?.value;
  const confirmPasswordControl = control.get("confirmPassword");
  const confirmPassword = confirmPasswordControl?.value;

  if (!confirmPasswordControl) return null;

  const mismatch = !!newPassword && !!confirmPassword && newPassword !== confirmPassword;

  const existingErrors = confirmPasswordControl.errors || {};
  if (mismatch) {
    confirmPasswordControl.setErrors({ ...existingErrors, passwordMismatch: true });
    return { passwordMismatch: true };
  }

  if (existingErrors["passwordMismatch"]) {
    const { passwordMismatch, ...rest } = existingErrors;
    confirmPasswordControl.setErrors(Object.keys(rest).length ? rest : null);
  }

  return null;
}

@Component({
  selector: "app-change-password-modal",
  templateUrl: "./change-password-modal.component.html",
  styleUrls: ["./change-password-modal.component.less"],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NzInputModule, NzButtonModule, NzIconModule],
})
export class ChangePasswordModalComponent {
  form: FormGroup;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  isSubmitting = false;
  passwordStrength = 0;
  passwordStrengthLabel = "";

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private notification: NzNotificationService,
    private modalRef: NzModalRef
  ) {
    this.form = this.fb.group(
      {
        currentPassword: ["", [Validators.required]],
        newPassword: ["", [Validators.required, Validators.minLength(6)]],
        confirmPassword: ["", [Validators.required]],
      },
      { validators: passwordMatchValidator }
    );

    this.form.get("newPassword")?.valueChanges.subscribe((value: string) => {
      this.updatePasswordStrength(value);
    });
  }

  private updatePasswordStrength(password: string): void {
    if (!password) {
      this.passwordStrength = 0;
      this.passwordStrengthLabel = "";
      return;
    }

    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 10) strength++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) strength++;

    this.passwordStrength = Math.max(1, strength);

    const labels: Record<number, string> = {
      1: "Faible",
      2: "Moyen",
      3: "Bon",
      4: "Excellent",
    };
    this.passwordStrengthLabel = labels[this.passwordStrength] || "";
  }

  toggleCurrent() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }
  toggleNew() {
    this.showNewPassword = !this.showNewPassword;
  }
  toggleConfirm() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  close() {
    this.modalRef.close();
  }

  submit() {
    if (this.isSubmitting) return;

    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((c) => {
        c.markAsDirty();
        c.updateValueAndValidity({ onlySelf: true });
      });
      return;
    }

    const { currentPassword, newPassword } = this.form.getRawValue();
    const payload = {
      currentPassword: sha256(currentPassword),
      newPassword: sha256(newPassword),
    };

    this.isSubmitting = true;
    this.auth.editPassword(payload).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        if (res.statusCode === 200 || res.statusCode === 201) {
          this.notification.success("Succès", "Mot de passe modifié avec succès.");
          this.modalRef.close({ success: true });
        } else {
          this.notification.error(
            "Erreur",
            res.statusMessage || "Une erreur est survenue lors de la modification."
          );
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        const msg =
          err?.error?.statusMessage ||
          err?.error?.message ||
          "Une erreur est survenue lors de la modification.";
        this.notification.error("Erreur", msg);
      },
    });
  }
}

